import React from "react";

import {
    Box,
    Fade,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
    CircularProgress
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import ReactMarkdown from "react-markdown";

import CustomButton from "../../helper-components/input/CustomButton";
import { sendChatMessageStream } from "../../helper-functions/api/chatQueries";
import { useLanguage } from "../../config/language/language";
import { prefsAreComplete } from "../../helper-functions/shared/guestPrefs";

const THINKING_ROTATE_MS = 2800;

const shuffleLines = (lines) => {
    const next = [...lines];
    for (let i = next.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j], next[i]];
    }
    return next;
};

const findLastUserIndex = (messages) => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        if (messages[i]?.role === "user") return i;
    }
    return -1;
};

export default function ChatPage(props) {
    const LANG = useLanguage();

    const isMobile = props.browser.isMobile;

    const [input, setInput] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [thinkingLines, setThinkingLines] = React.useState([]);
    const [thinkingIndex, setThinkingIndex] = React.useState(0);
    const [thinkingVisible, setThinkingVisible] = React.useState(true);
    const bottomRef = React.useRef(null);
    const handledAskIdRef = React.useRef(null);
    const streamContentRef = React.useRef("");

    const history = Array.isArray(props.chat.chatHistory) ? props.chat.chatHistory : [];
    const locationState = props.browser.location?.state || {};
    const drinkName = locationState.drinkName;
    const drinkAskId = locationState.drinkAskId;
    const autoPrompt = locationState.autoPrompt;
    const autoAskId = locationState.autoAskId;

    React.useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [props.chat.chatHistory, loading]);

    const sendMessage = React.useCallback(
        async (rawText, historyBefore = null) => {
            const text = (rawText || "").trim();
            if (!text || loading) return;

            const baseHistory = Array.isArray(historyBefore) ? historyBefore : history;
            const nextHistory = [...baseHistory, { role: "user", content: text }];
            props.chat.setChatHistory(nextHistory);
            setInput("");
            setLoading(true);
            streamContentRef.current = "";

            try {
                // Placeholder assistant bubble that grows as tokens arrive
                props.chat.setChatHistory([...nextHistory, { role: "assistant", content: "" }]);

                await sendChatMessageStream(props, nextHistory, {
                    onChunk: (_delta, full) => {
                        streamContentRef.current = full;
                        props.chat.setChatHistory([...nextHistory, { role: "assistant", content: full }]);
                    }
                });

                // Drop empty assistant bubble if the stream failed with no content
                if (!streamContentRef.current) {
                    props.chat.setChatHistory(nextHistory);
                }
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [history, loading, props]
    );

    React.useEffect(() => {
        if (loading) return;
        // Wait until welcome prefs are persisted so the API gets drinkType/tastes
        if (autoPrompt && !prefsAreComplete(props.prefs?.guestPrefs)) return;

        if (autoPrompt && autoAskId && handledAskIdRef.current !== autoAskId) {
            handledAskIdRef.current = autoAskId;
            sendMessage(autoPrompt);
            return;
        }

        if (drinkName && drinkAskId && handledAskIdRef.current !== drinkAskId) {
            handledAskIdRef.current = drinkAskId;
            sendMessage(LANG.chatDrinkPrompt(drinkName));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoPrompt, autoAskId, drinkName, drinkAskId, loading, props.prefs?.guestPrefs]);

    const lastUserIdx = findLastUserIndex(history);

    const handleEditLatest = () => {
        if (loading || lastUserIdx < 0) return;
        setInput(history[lastUserIdx].content || "");
        props.chat.setChatHistory(history.slice(0, lastUserIdx));
    };

    const handleRetryLatest = () => {
        if (loading || lastUserIdx < 0) return;
        const text = history[lastUserIdx].content || "";
        sendMessage(text, history.slice(0, lastUserIdx));
    };

    const handleSend = () => sendMessage(input);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const lastMsg = history[history.length - 1];
    const showThinking = loading && (!lastMsg || lastMsg.role !== "assistant" || !lastMsg.content);

    React.useEffect(() => {
        if (!showThinking) {
            setThinkingLines([]);
            setThinkingIndex(0);
            setThinkingVisible(true);
            return undefined;
        }

        const source =
            Array.isArray(LANG.CHAT_THINKING_LINES) && LANG.CHAT_THINKING_LINES.length
                ? LANG.CHAT_THINKING_LINES
                : [LANG.CHAT_THINKING];
        setThinkingLines(shuffleLines(source));
        setThinkingIndex(0);
        setThinkingVisible(true);

        let fadeTimer = null;
        const id = window.setInterval(() => {
            setThinkingVisible(false);
            fadeTimer = window.setTimeout(() => {
                setThinkingIndex((i) => i + 1);
                setThinkingVisible(true);
            }, 180);
        }, THINKING_ROTATE_MS);

        return () => {
            window.clearInterval(id);
            if (fadeTimer) window.clearTimeout(fadeTimer);
        };
    }, [showThinking, LANG]);

    const thinkingText =
        thinkingLines.length > 0
            ? thinkingLines[thinkingIndex % thinkingLines.length]
            : LANG.CHAT_THINKING;

    return (
        <Box sx={{ mt: 2, maxWidth: 800, mx: "auto", display: "flex", flexDirection: "column", height: isMobile ? "75vh" : "75vh" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">{LANG.CHAT_TITLE}</Typography>
                <CustomButton
                    {...props}
                    title={LANG.CHAT_CLEAR}
                    variant="outlined"
                    size="small"
                    onClick={() => props.chat.setChatHistory([])}
                    disabled={loading}
                />
            </Stack>

            <Paper
                variant="outlined"
                sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    p: 2,
                    mb: 2,
                    bgcolor: "background.paper"
                }}>
                {history.length === 0 && !loading && <Typography color="text.secondary">{LANG.CHAT_EMPTY}</Typography>}

                <Stack spacing={2}>
                    {history.map((msg, idx) => {
                        if (msg.role === "assistant" && !msg.content && loading) return null;
                        const isLatestUser = msg.role === "user" && idx === lastUserIdx;
                        return (
                            <Box
                                key={`${msg.role}-${idx}`}
                                sx={{
                                    alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                                    maxWidth: "90%"
                                }}>
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.25,
                                        borderRadius: 2,
                                        bgcolor: msg.role === "user" ? "primary.main" : "action.hover",
                                        color: msg.role === "user" ? "primary.contrastText" : "text.primary"
                                    }}>
                                    {msg.role === "assistant" ? (
                                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                                    ) : (
                                        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                                            {msg.content}
                                        </Typography>
                                    )}
                                </Box>
                                {isLatestUser && !loading && (
                                    <Stack direction="row" spacing={0.25} justifyContent="flex-end" sx={{ mt: 0.25 }}>
                                        <Tooltip title={LANG.CHAT_EDIT_HINT}>
                                            <IconButton
                                                size="small"
                                                aria-label={LANG.CHAT_EDIT}
                                                onClick={handleEditLatest}
                                                sx={{ color: "text.secondary" }}>
                                                <EditOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={LANG.CHAT_RETRY_HINT}>
                                            <IconButton
                                                size="small"
                                                aria-label={LANG.CHAT_RETRY}
                                                onClick={handleRetryLatest}
                                                sx={{ color: "text.secondary" }}>
                                                <ReplayIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                )}
                            </Box>
                        );
                    })}
                    {showThinking && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minHeight: 28 }}>
                            <CircularProgress size={20} />
                            <Fade in={thinkingVisible} timeout={180}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                    {thinkingText}
                                </Typography>
                            </Fade>
                        </Box>
                    )}
                    <div ref={bottomRef} />
                </Stack>
            </Paper>

            <Stack direction="row" spacing={1}>
                <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder={LANG.CHAT_PLACEHOLDER}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                />
                <CustomButton {...props} title={LANG.CHAT_SEND} variant="contained" onClick={handleSend} disabled={loading || !input.trim()} />
            </Stack>
        </Box>
    );
}
